import {
  ContactRound,
  Database,
  ExternalLink,
  MapPin,
  Search,
} from "lucide-react";
import { useState } from "react";
import {
  CopyableText,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableValue,
  FeedbackBanner,
  PageHeader,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";

export function ProfessionalSearchPage() {
  const api = useApi();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [sources, setSources] = useState([]);
  const [state, setState] = useState({
    loading: false,
    searched: false,
    error: "",
  });

  const search = async (event) => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2 || state.loading) return;

    setState({ loading: true, searched: false, error: "" });
    try {
      const data = await api.searchProfessionals(normalizedQuery);
      setResults(data.items || []);
      setSources(data.sources || []);
      setState({ loading: false, searched: true, error: "" });
    } catch (error) {
      setResults([]);
      setSources([]);
      setState({
        loading: false,
        searched: true,
        error: error.message,
      });
    }
  };

  const unavailableSources = sources.filter(
    (source) => source.status === "unavailable",
  );

  return (
    <div className="page tax-search-page instrumenta-directory-page">
      <PageHeader
        hero
        eyebrow="Instrumenta · Consulta profesional"
        title="Buscá un profesional o una matrícula."
        description="Consultá los padrones disponibles por nombre, apellido o número de matrícula y verificá siempre la jurisdicción de cada resultado."
      />

      <aside className="directory-coverage">
        <MapPin size={17} />
        <div>
          <strong>Cobertura disponible</strong>
          <span>
            Abogados y procuradores · Mendoza · Primera Circunscripción
          </span>
        </div>
      </aside>

      <form className="tax-search-form" onSubmit={search}>
        <label htmlFor="professional-query">
          Nombre, apellido o matrícula
          <span className="tax-search-control">
            <Search size={20} />
            <input
              id="professional-query"
              data-justiniano-target="professional-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ej. Apellido o 0000-A"
              autoComplete="off"
              autoFocus
            />
            <button
              className="primary-button"
              disabled={query.trim().length < 2 || state.loading}
            >
              {state.loading ? "Consultando padrones…" : "Buscar"}
            </button>
          </span>
        </label>
        <p>
          La fuente de Mendoza busca principalmente por apellido o número de
          matrícula. Podés escribir la matrícula con o sin su sufijo.
        </p>
      </form>

      {state.error && <FeedbackBanner>{state.error}</FeedbackBanner>}
      {unavailableSources.length > 0 && (
        <FeedbackBanner>
          Uno de los padrones no está disponible temporalmente. Los demás
          resultados se muestran con normalidad.
        </FeedbackBanner>
      )}

      <section className="tax-results" aria-live="polite">
        {results.length > 0 && (
          <div className="tax-results-heading">
            <div>
              <span className="eyebrow">Resultados</span>
              <h2>
                {results.length} coincidencia{results.length === 1 ? "" : "s"}
              </h2>
            </div>
            <Database size={20} />
          </div>
        )}

        {results.length > 0 && (
          <DataTable
            ariaLabel="Profesionales encontrados"
            className="directory-results-table"
            density="compact"
            minWidth="760px"
          >
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell className="directory-table-primary">
                  Profesional
                </DataTableHeaderCell>
                <DataTableHeaderCell>Jurisdicción</DataTableHeaderCell>
                <DataTableHeaderCell>Teléfono</DataTableHeaderCell>
                <DataTableHeaderCell align="end" className="directory-table-action">
                  Fuente
                </DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {results.map((professional) => {
                const registration = professional.registrations?.[0];
                return (
                  <DataTableRow key={professional.id}>
                    <DataTableCell>
                      <DataTablePrimaryCell
                        icon={<ContactRound size={16} />}
                        eyebrow={registration?.number ? (
                          <CopyableText
                            copiedMessage="Matrícula copiada"
                            label="matrícula"
                            value={registration.number}
                          >
                            Matrícula {registration.number}
                          </CopyableText>
                        ) : "Matrícula no informada"}
                        title={professional.fullName}
                      />
                    </DataTableCell>
                    <DataTableCell>
                      <DataTableValue
                        primary={professional.source?.jurisdictionLabel || "No informada"}
                        secondary={professional.source?.label || "Padrón profesional"}
                      />
                    </DataTableCell>
                    <DataTableCell>
                      <DataTableValue
                        primary={professional.contact?.phone ? (
                          <CopyableText
                            copiedMessage="Teléfono copiado"
                            label="teléfono"
                            value={professional.contact.phone}
                          />
                        ) : "No informado"}
                      />
                    </DataTableCell>
                    <DataTableCell align="end">
                      {professional.source?.url ? (
                        <a
                          className="directory-table-link"
                          href={professional.source.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Ver padrón
                          <ExternalLink size={14} />
                        </a>
                      ) : "—"}
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        )}

        {state.searched && !state.error && !results.length && (
          <div className="tax-empty-state">
            <Search size={25} />
            <h2>No encontramos coincidencias</h2>
            <p>
              Probá con el apellido solamente o con el número de matrícula sin
              el sufijo.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
